'use client';
import { useState } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Link from 'next/link';
import { ArrowLeft, Calendar, Calculator, Check, Copy } from 'lucide-react';

export default function DateRangeCalculatorPage() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [result, setResult] = useState('');
  const [copied, setCopied] = useState(false);

  const calculateRange = () => {
    if (!startDate || !endDate) {
      setResult('Please select both dates');
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    const years = Math.floor(diffDays / 365);
    const months = Math.floor((diffDays % 365) / 30);
    const days = diffDays % 30;

    setResult(`Difference: ${diffDays} days\n\nApproximately:\n- ${years} years\n- ${months} months\n- ${days} days`);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(result);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="marketing-theme tool-page">

      <Navbar />

      <main className="tool-page-main flex flex-col items-center">
        <div className="w-full max-w-5xl">
          <Link 
            href="/tools" 
            className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:underline mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tools
          </Link>

          <div className="text-center mb-8">
              <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Date Range Calculator
              </h1>
            <p className="text-lg text-muted-foreground">
              Calculate the number of days, months, and years between two dates
            </p>
          </div>

          <div className="bg-card rounded-xl border p-8 shadow-sm max-w-2xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center">
                  <Calendar className="w-4 h-4 mr-2 text-blue-500" />
                  Start Date
                </label>
                <Input 
                  type="date" 
                  value={startDate} 
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center">
                  <Calendar className="w-4 h-4 mr-2 text-purple-500" />
                  End Date
                </label>
                <Input 
                  type="date" 
                  value={endDate} 
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>

            <Button 
              onClick={calculateRange} 
              className="w-full py-6 text-lg mb-6"
              size="lg"
            >
              <Calculator className="w-5 h-5 mr-2" />
              Calculate Difference
            </Button>

            {result && (
              <div className="p-6 bg-muted rounded-lg relative">
                <pre className="font-mono text-lg whitespace-pre-wrap">{result}</pre>
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={copyToClipboard}
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>

      <section className="max-w-4xl mx-auto mt-16 mb-12 p-6 bg-card rounded-lg border border-gray-200 dark:border-gray-700">
        <h2 className="text-2xl font-bold mb-4">Date Range FAQs</h2>
        <div className="space-y-6">
          <div><p className="font-semibold">1. How is the difference calculated?</p><p className="text-muted-foreground">The tool calculates the absolute difference in milliseconds and converts it to days, months, and years.</p></div>
          <div><p className="font-semibold">2. Does it include the end date?</p><p className="text-muted-foreground">Yes, it calculates the total duration between the two dates.</p></div>
          <div><p className="font-semibold">3. Can I calculate dates in the past?</p><p className="text-muted-foreground">Yes, you can select any two dates, and it will provide the absolute difference.</p></div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
